import {useMutation, useQueryClient} from '@github-ui/react-query'
import {commentEditHistoryQueryKey} from '@github-ui/markdown-edit-history-viewer/fetch-comment-edit-history'
import {markdownEditHistoryMetadataQueryKey} from '@github-ui/markdown-edit-history-viewer/fetch-markdown-edit-history-metadata'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {descriptionQueryKey, type DescriptionData} from '../page-data/loaders/use-description-data'
import {useLayoutPullRequest} from '../stores/layout-selectors'
import {
  fetchWithErrorHandling,
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'

/**
 * Updates the description (body) of a pull request. On success, will update Tanstack query data
 * for the pull request description and edit attribution to reflect the update.
 *
 * @example
 * ```ts
 * const {mutate} = useUpdateDescriptionMutation()
 * mutate({id: pullRequest.id, body: 'new description'})
 * ```
 */
export function useUpdateDescriptionMutation(onError?: (error: Error) => void) {
  const queryClient = useQueryClient()
  const {relayId} = useLayoutPullRequest()
  const url = usePageDataUrl(PageData.updateDescription)
  const descriptionURL = usePageDataUrl(PageData.description)
  const descriptionKey = descriptionQueryKey(descriptionURL)

  return useMutation({
    networkMode: 'always',
    mutationFn: async ({body}: {body: string}) => {
      const response = await fetchWithErrorHandling(url, {method: 'PATCH', body: {body}})

      const json = await parseJSONWithBetterErrors(response)

      // TODO: Currently this endpoint does not accept a `body_version` parameter so it cannot (and does not try to)
      // detect stale edits. We should update the endpoint, then update the callsite to pass the body version hash.
      // For reference see `/packages/pull-requests/mutations/use-update-issue-comment-mutation.ts`
      // and `/packages/pull-requests/components/comments/use-body-edit-manager.ts`
      // if (response.status === 422 && json.stale) throw new StaleContentError()

      throwErrorsIfBadResponse(response, json)

      return json as DescriptionData
    },
    onSuccess: result => {
      queryClient.setQueryData<DescriptionData>(descriptionKey, result)
      if (!relayId) return
      void queryClient.invalidateQueries({queryKey: markdownEditHistoryMetadataQueryKey(relayId)})
      void queryClient.invalidateQueries({queryKey: commentEditHistoryQueryKey(relayId)})
    },
    onError: async error => {
      // TODO: see note above
      // if (error instanceof StaleContentError) {
      //   await queryClient.invalidateQueries({queryKey: descriptionKey})
      // }
      onError?.(error)
    },
  })
}
